import React, { useEffect } from "react";
import { useTranslate } from "@tolgee/react";
import DataTable from "react-data-table-component";

import axios from "axios";
import {
  FACEBOOK,
  FACEBOOK_GROUP,
  GOOGLE,
  INSTAGRAM,
  LINKEDIN_PERSONAL_ACCOUNT,
  PLATFORMS_COLORS,
  TIKTOK_PERSONAL_ACCOUNT,
  TWITTER,
  YOUTUBE,
} from "../../../../../config/constants";
import {
  LINKEDIN,
  META,
  URL_PARAM_CONNECTING_SOCIAL_MEDIA_USER_UUID,
  URL_PARAM_PLATFORM,
} from "../../../constants";
import { PATH_PAGE } from "../../../../../routes/paths";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faLink, faSpinner } from "@fortawesome/pro-duotone-svg-icons";
import ConfirmModal from "../../../../../components/confirmModal";
import { Alert } from "react-bootstrap";
import { PLATFORM_STATIC_ICONS } from "../../../../../config/icons";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

const ConnectedSocialMediaAccountsListView = () => {
  const { t } = useTranslate();
  const [connectedSocialMediaAccounts, setConnectedSocialMediaAccounts] =
    React.useState(null);
  const [
    connectedSocialMediaAccountsLoaded,
    setConnectedSocialMediaAccountsLoaded,
  ] = React.useState(false);

  const [
    connectedSocialMediaAccountsError,
    setConnectedSocialMediaAccountsError,
  ] = React.useState(null);

  const [redirectUrlAfterConfirm, setRedirectUrlAfterConfirm] =
    React.useState(null);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);

  const ICONS_TO_SHOW_FOR_PLATFORM = {
    [FACEBOOK]: [
      PLATFORM_STATIC_ICONS[FACEBOOK],
      PLATFORM_STATIC_ICONS[INSTAGRAM],
    ],
    [LINKEDIN_PERSONAL_ACCOUNT]: [
      PLATFORM_STATIC_ICONS[LINKEDIN_PERSONAL_ACCOUNT],
    ],
    [GOOGLE]: [PLATFORM_STATIC_ICONS[GOOGLE], PLATFORM_STATIC_ICONS[YOUTUBE]],
    [TIKTOK_PERSONAL_ACCOUNT]: [PLATFORM_STATIC_ICONS[TIKTOK_PERSONAL_ACCOUNT]],
    [TWITTER]: [PLATFORM_STATIC_ICONS[TWITTER]],
  };

  const getUrlPlatformParamForPlatform = (platform) => {
    if ([FACEBOOK, INSTAGRAM, FACEBOOK_GROUP].indexOf(platform) !== -1) {
      return META;
    }
    if (
      [LINKEDIN_PERSONAL_ACCOUNT, LINKEDIN_PERSONAL_ACCOUNT].indexOf(
        platform
      ) !== -1
    ) {
      return LINKEDIN;
    }
    if ([GOOGLE, YOUTUBE].indexOf(platform) !== -1) {
      return GOOGLE;
    }
    return platform;
  };

  const handleConfirm = () => {
    // Handle the confirm action here
    window.location.href = redirectUrlAfterConfirm;
    setShowConfirmModal(false);
  };

  const handleClose = () => {
    setShowConfirmModal(false);
    setSelectedRow(null);
  };

  const loadConnectedSocialMediaAccounts = async () => {
    if (connectedSocialMediaAccounts) {
      return;
    }

    return new Promise<any>(async (resolve, reject) => {
      let finalResult = [];
      let url = `/api/v1/platforms/accounts/connected_social_media_accounts/`;

      while (url) {
        try {
          const response = await axios.get(url);
          url = response.data.next;

          finalResult = finalResult.concat(response.data.results);
          finalResult = finalResult.sort((a, b) =>
            a.platform_display_name < b.platform_display_name ? 1 : -1
          );

          setConnectedSocialMediaAccounts(finalResult);
          setConnectedSocialMediaAccountsLoaded(true);

          setConnectedSocialMediaAccountsError(
            finalResult.filter(
              (row) =>
                row?.access_token_is_valid === false ||
                row?.account_needs_reconnect
            )
          );

          console.log("Facebook pages fetched", finalResult);
        } catch (error) {
          console.error("Error fetching facebook pages", error);
          reject(error);
        }
      }
    });
  };

  const getOpenHref = (row) => {
    return `${
      PATH_PAGE.platforms_connected_social_media_accounts_connected_profiles
    }?${URL_PARAM_PLATFORM}=${getUrlPlatformParamForPlatform(
      row.platform
    )}&${URL_PARAM_CONNECTING_SOCIAL_MEDIA_USER_UUID}=${row.uuid}`;
  };

  const columns = [
    {
      name: (
        <>
          <OverlayTrigger
            overlay={
              <Tooltip>{t("connectedSocialMediaAccounts.platform")}</Tooltip>
            }
          >
            <div>{t("connectedSocialMediaAccounts.platform")}</div>
          </OverlayTrigger>
        </>
      ),
      cell: (row) => (
        <a href={getOpenHref(row)}>
          <div
            className={"fw-bolder"}
            style={{ color: row.platform_color_code }}
          >
            {row.platform_display_name}
          </div>
        </a>
      ),
    },
    {
      name: "",
      cell: (row) => (
        <div>
          {ICONS_TO_SHOW_FOR_PLATFORM[row.platform].map((icon, index) => (
            <img className={"h-20px me-1"} src={icon} />
          ))}
        </div>
      ),
    },
    {
      name: (
        <>
          <OverlayTrigger
            overlay={
              <Tooltip>{t("connectedSocialMediaAccounts.profiles")}</Tooltip>
            }
          >
            <div>{t("connectedSocialMediaAccounts.profiles")}</div>
          </OverlayTrigger>
        </>
      ),
      cell: (row) => (
        <div className={"d-flex justify-content-between"}>
          <span>{row.connected_profile_count}</span>
        </div>
      ),
    },
    {
      name: (
        <>
          <OverlayTrigger
            overlay={
              <Tooltip>{t("connectedSocialMediaAccounts.status")}</Tooltip>
            }
          >
            <div>{t("connectedSocialMediaAccounts.status")}</div>
          </OverlayTrigger>
        </>
      ),
      cell: (row) => (
        <>
          {row.account_needs_reconnect && row.access_token_is_valid ? (
            <OverlayTrigger
              overlay={
                <Tooltip>
                  {t("connectedSocialMediaAccounts.reconnectRequiredTooltip")}
                </Tooltip>
              }
            >
              <div className={"badge bg-warning text-white"}>
                {t("connectedSocialMediaAccounts.reconnectRequired")}
              </div>
            </OverlayTrigger>
          ) : row.access_token_is_valid ? (
            <div className={"badge bg-success text-white"}>
              {t("connectedSocialMediaAccounts.active")}
            </div>
          ) : (
            <div className={"badge bg-danger text-white"}>
              {t("connectedSocialMediaAccounts.error")}
            </div>
          )}
        </>
      ),
    },
    {
      name: (
        <>
          <OverlayTrigger
            overlay={
              <Tooltip>
                {t("connectedSocialMediaAccounts.platformUsername")}
              </Tooltip>
            }
          >
            <div>{t("connectedSocialMediaAccounts.platformUsername")}</div>
          </OverlayTrigger>
        </>
      ),
      cell: (row) => (
        <div>
          {row.obscured_platform_username.length > 0
            ? row.obscured_platform_username
            : "-"}
        </div>
      ),
    },
    {
      name: (
        <>
          <OverlayTrigger
            overlay={
              <Tooltip>
                {t("connectedSocialMediaAccounts.platformEmail")}
              </Tooltip>
            }
          >
            <div>{t("connectedSocialMediaAccounts.platformEmail")}</div>
          </OverlayTrigger>
        </>
      ),
      cell: (row) => (
        <div>
          {row.obscured_email_address.length > 0
            ? row.obscured_email_address
            : "-"}
        </div>
      ),
    },
    {
      name: (
        <>
          <OverlayTrigger
            overlay={
              <Tooltip>{t("connectedSocialMediaAccounts.lomavisUser")}</Tooltip>
            }
          >
            <div>{t("connectedSocialMediaAccounts.lomavisUser")}</div>
          </OverlayTrigger>
        </>
      ),
      cell: (row) => <div>{row.user}</div>,
    },
    {
      name: (
        <>
          <OverlayTrigger
            overlay={
              <Tooltip>{t("connectedSocialMediaAccounts.action")}</Tooltip>
            }
          >
            <div>{t("connectedSocialMediaAccounts.action")}</div>
          </OverlayTrigger>
        </>
      ),
      cell: (row) => (
        <div className={""}>
          <div
            className={`btn btn-sm btn-primary ${
              selectedRow ? "disabled" : ""
            }`}
            style={{
              whiteSpace: "nowrap",
            }}
            onClick={() => {
              if (!selectedRow) {
                setSelectedRow(row);
                setRedirectUrlAfterConfirm(row.reconnect_account_url);
                setShowConfirmModal(true);
              }
            }}
          >
            {selectedRow === row ? (
              <i className="fa-solid fa-spinner fa-spin"></i>
            ) : (
              t("connectedSocialMediaAccounts.reconnect")
            )}
          </div>
        </div>
      ),
    },
    {
      name: (
        <>
          <OverlayTrigger
            overlay={
              <Tooltip>{t("connectedSocialMediaAccounts.show")}</Tooltip>
            }
          >
            <div>{t("connectedSocialMediaAccounts.show")}</div>
          </OverlayTrigger>
        </>
      ),
      cell: (row) => (
        <span>
          <a className={"btn btn-warning btn-sm"} href={getOpenHref(row)}>
            {t("connectedSocialMediaAccounts.show")}
          </a>
        </span>
      ),
    },
  ];

  const init = () => {
    loadConnectedSocialMediaAccounts();
  };

  useEffect(() => {
    init();
  });

  return (
    <div className={"container"}>
      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        title={t("connectedSocialMediaAccountsModal.title")}
        message={t("connectedSocialMediaAccountsModal.message")}
        showNextButton={true}
      />

      <div>
        <Alert variant={"primary"} className={"text-center"}>
          {t("connectedSocialMediaAccounts.info")}
        </Alert>
      </div>

      {connectedSocialMediaAccountsError &&
        connectedSocialMediaAccountsError.length > 0 && (
          <div className="card card-flush h-md-100 mb-5">
            <div className="card-header pt-7">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold text-gray-800">
                  {t("connectedSocialMediaAccounts.CardTitleError")}
                </span>
              </h3>
            </div>

            <div className="card-body pt-6">
              {connectedSocialMediaAccountsError && (
                <DataTable
                  columns={columns}
                  data={connectedSocialMediaAccountsError}
                  pagination
                  paginationComponentOptions={{
                    rangeSeparatorText: t(
                      "connectedSocialMediaAccounts.table.of"
                    ),
                    rowsPerPageText: t(
                      "connectedSocialMediaAccounts.table.entries_per_page"
                    ),
                  }}
                />
              )}
            </div>
          </div>
        )}

      {
        <div className="card card-flush h-md-100">
          <div className="card-header pt-7">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold text-gray-800">
                {t("connectedSocialMediaAccounts.CardTitle")}
              </span>
            </h3>
          </div>

          <div className="card-body pt-6">
            {connectedSocialMediaAccountsLoaded && (
              <DataTable
                columns={columns}
                data={connectedSocialMediaAccounts}
                pagination
                paginationComponentOptions={{
                  rangeSeparatorText: t(
                    "connectedSocialMediaAccounts.table.of"
                  ),
                  rowsPerPageText: t(
                    "connectedSocialMediaAccounts.table.entries_per_page"
                  ),
                }}
              />
            )}
          </div>
        </div>
      }
    </div>
  );
};

export default ConnectedSocialMediaAccountsListView;
